import { useState } from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import {
  colors,
  fontWeight,
  Heading,
  headingLevel,
  Input,
  inputSize,
  Text,
  TextError,
  textLevel,
} from '../../../../packages';
import { Drawer, drawerPosition, drawerType } from '../../../../packages/ui/organisms/drawer';
import { ProfileInfo } from '../../../../packages/ui/templates/profile-info';
import { useStyles } from '../ClaimDetail.styles';
import { Typography } from '@mui/material';

const defaultData = {
  uPin: '',
  employerId: '',
  firstName: '',
  fullName: '',
  lastName: '',
  npi: '',
  profileImage: '',
  stateLicense: '',
  userAcccountId: '',
};

const labels = {
  fullName: 'Full name',
  firstName: 'First name',
  lastName: 'Last name',
  npi: 'NPI',
  // employerId: 'Employer ID',
  // stateLicense: 'State license',
  // uPin: 'UPIN',
};

const ProviderDrawer = ({ data, open, onClose, onSubmit }) => {
  const classes = useStyles();
  const [formData, setFormData] = useState({ ...data });
  const [errors, setErrors] = useState(defaultData);

  const onChangeField = field => e => {
    if (errors[field]) {
      setErrors(s => ({ ...s, [field]: null }));
    }
    setFormData(old => ({
      ...old,
      [field]: e.target?.value,
    }));
  };

  const onChangeBillProvider = () => {
    onSubmit(formData, 'billingProvider');
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      variant={drawerType.FORM}
      position={drawerPosition.RIGHT}
      title="Edit Provider"
      submitBtnTitle="Update"
      onSubmit={onChangeBillProvider}
    >
      {Object.keys(labels).map(d => (
        <Box className={classes.inputWrapper} key={d}>
          <label htmlFor={d}>
            <Text level={textLevel.S} weight={fontWeight.BOLD}>
              {labels[d]}
            </Text>
          </label>
          <Input
            name={d}
            value={formData[d] || ''}
            onChange={onChangeField(d)}
            size={inputSize.M}
            placeholder={`Enter your ${labels[d]}`}
          />
          {errors[d] && <TextError errorMsg={errors[d]} />}
        </Box>
      ))}
    </Drawer>
  );
};

const Provider = ({ onSubmit, data = defaultData, type }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const SectionTitle = ({ title = '' }) => (
    <Typography
      sx={{
        fontSize: '12px',
        fontWeight: 800,
        lineHeight: '20px',
        letterSpacing: '1px',
        width: '100%',
        textTransform: 'uppercase',
        color: colors.neutral900,
      }}
    >
      {title}
    </Typography>
  );
  const Col = ({ label, value }) => (
    <Box display="flex" flexDirection="column" minWidth={160}>
      <Text className={classes.colLabel} level={textLevel.S} weight={fontWeight.SEMI_BOLD}>
        {label}
      </Text>
      <Text className={classes.marginTop8} level={textLevel.M} weight={fontWeight.MEDIUM}>
        {value || 'N/A'}
      </Text>
    </Box>
  );

  return (
    <Box
      sx={{
        background: colors.white,
        boxShadow:
          '0px 4px 8px rgba(0, 0, 0, 0.04), 0px 0px 2px rgba(0, 0, 0, 0.06), 0px 0px 1px rgba(0, 0, 0, 0.04)',
        borderRadius: 2,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" pl={3} py="12px" pr={2}>
        <Heading level={headingLevel.S} weight={fontWeight.SEMI_BOLD}>
          Rendering provider
        </Heading>
      </Box>
      <Divider />

      <Box display="flex" p={4} flexWrap="wrap" gap={3} rowGap={4}>
        <Box display="flex" flexDirection="column" minWidth={185} mr={4}>
          <ProfileInfo
            type="provider"
            role={data?.designation}
            photo={data?.profileImage}
            nickName={data?.fullName || ''}
            fullName={`${data?.firstName ? `${data?.firstName} ` : ''}${data?.lastName || ''}`}
            memberId={data?.userAccountId}
          />
        </Box>
        <Col label="First Name" value={data?.firstName} />
        <Col label="Last Name" value={data?.lastName} />
        <Col label="NPI" value={data?.npi} />
        {data?.taxonomyCode && <Col label="Taxonomy Code" value={data?.taxonomyCode} />}
      </Box>
      <Box display="flex" px={4} pb={4} flexWrap="wrap" gap={3} rowGap={4}>
        <SectionTitle title="Service Location" />
        <Col
          label="Organization Name"
          value={data?.serviceLocation?.organization_name ?? data?.organizationName}
        />
        {data?.serviceLocation?.address && (
          <>
            <Col
              label="Address 1 & 2"
              value={
                (data?.serviceLocation?.address?.address1 || '') +
                ' ' +
                (data?.serviceLocation?.address?.address2 || '')
              }
            />
            <Col label="City" value={data?.serviceLocation?.address?.city} />
          </>
        )}
        <Col label="State" value={data?.serviceLocation?.address?.state ?? data?.organizationState} />
        <Col label="Zip Code" value={data?.serviceLocation?.address?.zip_code ?? 'N/A'} />
      </Box>
      {open && <ProviderDrawer data={data} open={open} onClose={() => setOpen(false)} onSubmit={onSubmit} />}
    </Box>
  );
};

export default Provider;
